import _ from "lodash";

import { mapState, mapGetters } from "vuex";

import Event from "@/services/Event";
import Enrollment from "@/services/Enrollment";

import ParseHTTPError from "@/helpers/ParseHTTPError";

import EnrollmentChip from "@/components/Enrollments/Chip";
import FormatDate from "@/helpers/FormatDate";

import EventEditor from "@/components/Events/Editor";
import EnrollmentSubmitCoach from "@/components/Enrollments/Submit/Coach";
import EnrollmentSubmitMembers from "@/components/Enrollments/Submit/Members";
import EnrollmentSubmitDocuments from "@/components/Enrollments/Submit/Documents";
import EnrollmentSubmitTerms from "@/components/Enrollments/Submit/Terms";
import QueuedUploadList from "@/components/QueuedUploadList";

import TeamEditor from "@/components/Teams/Editor";
import UserEditor from "@/components/Users/Editor";

import getJudgeTypes from "@/helpers/events/getJudgeTypes";
import getPossibleStages from "@/helpers/events/getPossibleStages";
import getCurrentSeason from "@/helpers/events/getCurrentSeason";
import getMaxMembers from "@/helpers/events/getMaxMembers";
import getMinMembers from "@/helpers/events/getMinMembers";

export default {
  name: "Submit",

  data() {
    return {
      // Loaders flags
      oldModel: "",
      saving: false,
      loading: false,

      hasErrorsInAutosave: false,
      lastSave: null,

      event: null,
      model: {},
    };
  },

  props: {
    cache: {
      type: Object,
    },

    modelId: {
      type: String,
      required: true,
    },

    impersonate: {
      type: Boolean,
      default: false,
    },

    kind: {
      type: String,
      default: "team",
    },
  },

  components: {
    EnrollmentChip,
    EnrollmentSubmitCoach,
    EnrollmentSubmitMembers,
    EnrollmentSubmitDocuments,
    EnrollmentSubmitTerms,
    QueuedUploadList,

    TeamEditor,
    UserEditor,
  },

  created() {
    // Set current model to show model faster (Cache version)
    if (this.cache && this.modelId == this.cache._id) {
      this.model = this.cache;
    }

    if (!this.modelId) {
      this.$router.push({ name: "home" });
      return;
    }

    // Fetch model events and data
    this.fetch();
  },

  watch: {
    model: {
      deep: true,
      handler(val) {
        // If enrollment cann't modify
        if (!this.isEditable && !this.isSuboperator && !this.isChiefJudge)
          return;

        // Check if there are any uploads
        const uploadPaths = ["documents.permissions"];
        for (const path of uploadPaths) {
          if (_.get(this.model, path, []).some((m) => !m.path)) {
            // An uploading is happenning
            return;
          }
        }

        const valJson = JSON.stringify(val);
        if (valJson && this.oldModel && valJson != this.oldModel) {
          this.autoSaveIfNeeded();
        }
        this.oldModel = valJson;
      },
    },
  },

  computed: {
    isJudgeKind() {
      return this.kind == "judge";
    },

    isTeamKind() {
      return this.kind == "team";
    },

    isImpersonator() {
      return this.impersonate;
    },

    ...mapState(["enrollmentStatuses", "community", "isJudge"]),
    ...mapGetters(["isSuboperator", "isChiefJudge", "isCommittee"]),

    judgeKinds() {
      return getJudgeTypes(this.community).map((judgeType) => ({
        ...judgeType,
        active: true,
        kind: judgeType._id,
        title: judgeType.label,
      }));
    },

    tournamentStages() {
      return _.map(getPossibleStages(this.community), "kind");
    },

    eventStage() {
      return _.get(this.event, "event.kind._id");
    },

    nextStage() {
      const possibleStages = getPossibleStages(this.community);
      const currentStage = _.find(possibleStages, { kind: this.eventStage });
      if (!currentStage) return null;
      return _.find(possibleStages, { level: currentStage.level + 1 });
    },

    defaults() {
      return {
        event: {},
        team: {
          coach: [],
          members: [],
          mainCoach: [],
        },
        documents: {
          permissions: [],
          acceptReponsability: {},
          acceptRules: {},
        },
        judge: {
          area: [],
        },
        entity: {},
        situation: {
          status: {},
        },
      };
    },

    saveLabel() {
      if (this.saving) {
        return "Salvando...";
      }
      if (this.hasErrorsInAutosave && !this.lastSave) {
        return "Alterações não estão salvas. Salvar agora";
      }
      if (this.hasErrorsInAutosave && this.lastSave) {
        return `Alterações não são salvas ${
          this.lastSave && FormatDate(this.lastSave, "fromNow")
        }. Salvar agora`;
      }
      if (this.lastSave) {
        return `Salvo ${FormatDate(this.lastSave, "fromNow")}`;
      }

      return "As alterações são salvas automaticamente";
    },

    enrollmentStatusColor() {
      if (!_.get(this.model, "situation.status._id")) return "tournament-red";

      return {
        waiting: "tournament-red",
        reviewing: "tournament-yellow",
        confirmed: "tournament-green",
        finished: "tournament-grey",
        rejected: "tournament-black",
        pending_payment: "tournament-yellow",
      }[_.get(this.model, "situation.status._id")];
    },

    enrollmentStatusLabel() {
      if (!this.event) return "";

      const subscriptionEnd = _.get(this.event, "registration.subscriptionEnd");
      // const enrollmentStatus = _.get(this.model, 'situation.status._id');

      const label = {
        waiting: subscriptionEnd
          ? `Você tem até o dia ${FormatDate(
              subscriptionEnd,
              "longerDate",
            )} para completar as informações abaixo.`
          : "Você pode alterar às informações até a data informada pela organização",
        reviewing: "Sua inscrição está sendo analisada.",
        confirmed: "Sua inscrição foi aprovada.",
        rejected: "Sua inscrição foi rejeitada",
        canceled: "Sua inscrição foi cancelada",
        alternate:
          "Você só poderá completar seu cadastro caso haja alguma desistência",
        pending_payment: "Aguardando pagamento",
      }[_.get(this.model, "situation.status._id")];

      return label;
    },

    isEditable() {
      return (
        _.get(this.model, "situation.status._id") == "waiting" ||
        this.isSuboperator
      );
    },

    minMembers() {
      return getMinMembers(this.community);
    },

    maxMembers() {
      return getMaxMembers(this.community);
    },

    memberCountOk() {
      const n = _.get(this.model, "team.members.length", 0);
      return n >= this.getMinMembers && n <= this.maxMembers;
    },

    coachImageAuthorizationURL() {
      return {
        "fll-brasil": "https://layers.land/fll-tecnico",
        "ftc-brasil": "https://layers.land/ftc-tecnico",
        "frc-brasil": "https://layers.land/frc-tecnico",
        "f1-brasil": "https://layers.land/f1-tecnico",
        desafiocovid:
          "https://cdn.layers.digital/admin/uploads/f43eb96b-46aa-4e57-b476-3ff118921109/termo_autorizacao_tecnico.pdf",
        desafiorelampago:
          "https://link.tenda.digital/desafiorelampago/termo_do_tecnico",
        "fll-open-brazil": "https://link.layers.education/coach-authorization",
        "off-season-sesi-mt": "https://layers.land/fll-tecnico",
      }[this.community];
    },

    coachTermAuthorizationURL() {
      return {
        "f1-brasil": "https://layers.land/f1-responsabilidade",
        "ftc-brasil": "https://layers.land/ftc-juiz-adesao",
      }[this.community];
    },

    membersAuthorizationDocumentURL() {
      return {
        "fll-brasil": "https://layers.land/fll-competidor",
        "ftc-brasil": "https://layers.land/ftc-competidor",
        "frc-brasil": "https://layers.land/frc-competidor",
        "f1-brasil": "https://layers.land/f1-competidor",
        desafiocovid:
          "https://cdn.layers.digital/admin/uploads/fb077c87-a18a-4c96-ad1a-5afb21a23e50/termo_de_imagem.pdf",
        desafiorelampago:
          "https://link.tenda.digital/desafiorelampago/termo_menor_de_idade",
        "fll-open-brazil":
          "https://link.layers.education/members-authorization",
        "off-season-sesi-mt": "https://layers.land/fll-competidor",
      }[this.community];
    },

    adultsMembersAuthorizationDocumentURL() {
      return {
        "frc-brasil": "https://layers.land/frc-competidor",
        desafiorelampago:
          "https://link.tenda.digital/desafiorelampago/termo_maior_de_idade",
      }[this.community];
    },

    partnershipTermURL() {
      return {
        "f1-brasil": "https://layers.land/f1-parcerias",
      }[this.community];
    },

    responsabilityTermURL() {
      return {
        "f1-brasil": "https://layers.land/f1-plagio",
      }[this.community];
    },

    judgeImageAuthorizationURL() {
      return {
        "fll-brasil": "https://layers.land/fll-juiz",
        "ftc-brasil": "https://layers.land/ftc-juiz-imagem",
        "f1-brasil": "https://layers.land/f1-juiz-imagem",
        "frc-brasil": "https://layers.land/frc-juiz",
        "off-season-sesi-mt": "https://layers.land/fll-juiz",
      }[this.community];
    },

    judgeStatementResponsabilityURL() {
      return {
        "fll-brasil": "https://layers.land/fll-juiz",
        "ftc-brasil": "https://layers.land/ftc-juiz-adesao",
        "f1-brasil": "https://layers.land/f1-juiz-adesao",
        "frc-brasil": "https://layers.land/frc-juiz",
        "off-season-sesi-mt": "https://layers.land/fll-juiz",
      }[this.community];
    },
  },

  methods: {
    goBack() {
      return this.$router.back();
    },

    showEvent(event) {
      if (!_.get(event, "_id")) {
        return;
      }

      this.$dialog(
        {
          width: this.$vuetify.breakpoint.xsOnly ? "95%" : "608px",
          cache: event,
          modelId: event._id,
        },
        EventEditor,
      )
        .then(() => {})
        .catch(() => {});
    },

    fetch() {
      this.loading = true;

      Enrollment.get(this.modelId).then(
        (res) => {
          this.model = _.defaultsDeep(res.data, this.defaults);
          this.oldModel = JSON.stringify(this.model);

          this.fetchEvent();
        },
        () => {
          this.loading = false;
          this.$notify.error({
            title: "Erro",
            message: "Inscrição não encontrada",
          });
          this.goBack();
        },
      );
    },

    fetchEvent() {
      const eventId = _.get(this.model, "event._id");

      if (!eventId) return;
      Event.get(eventId).then(
        (res) => {
          this.loading = false;

          this.event = res.data;
        },
        () => {
          this.loading = false;

          this.$notify.error({
            title: "Erro",
            message: "Evento não encontrado",
          });
          this.goBack();
        },
      );
    },

    submitForm() {
      this.$validator.validateAll().then((result) => {
        // If throw errors in form, return
        if (!result) {
          this.$notify.error({
            title: "Encontramos alguns erros",
            message: this.$createElement("Error", {
              props: {
                error: this.errors,
              },
            }),
          });
          return;
        }
        this.validate();
      });
    },

    editTeam(team) {
      if (!_.get(team, "_id")) {
        return;
      }

      this.$dialog(
        {
          width: this.$vuetify.breakpoint.xsOnly ? "95%" : "608px",
          cache: team,
          modelId: team._id,
          closeOnClickModal: false,
        },
        TeamEditor,
      )
        .then(() => {
          this.fetch();
        })
        .catch(() => {});
    },

    validate() {
      this.saving = true;
      Enrollment.validate(this.model, this.modelId).then(
        () => {
          this.$dialog({
            width: this.$vuetify.breakpoint.xsOnly ? "95%" : "330px",
            title: "Deseja submeter agora?",
            content:
              "Se você confirmar, não será possível fazer alterações nessa inscrição",
            buttons: [
              { name: "Depois", class: "tournament-semi-black--text ma-0" },
              {
                name: "Submeter Agora",
                action: "submit",
                class: "tournament-green--text ma-0",
              },
            ],
            customClass: "tournament-white pa-4",
            top: "30vh",
          })
            .then(() => {
              this.submit();
            })
            .catch(() => {
              this.saving = false;
            });
        },
        (err) => {
          this.saving = false;
          const status = ParseHTTPError(err);
          let { error } = status;

          if (!status.type) {
            error =
              "Houve um erro na comunicação com o servidor, tente novamente";
          }

          this.$notify.error({
            title: "Erro",
            message: error,
          });
        },
      );
    },

    submit() {
      Enrollment.submit(this.modelId).then(
        (res) => {
          this.model = _.defaultsDeep(res.data, this.defaults);
          this.saving = false;
        },
        (err) => {
          this.saving = false;
          const status = ParseHTTPError(err);
          let { error } = status;

          if (!status.type) {
            error =
              "Houve um erro na comunicação com o servidor, tente novamente";
          }

          this.$notify.error({
            title: "Erro",
            message: error,
          });
        },
      );
    },

    async autosave() {
      this.saving = true;
      try {
        const res = await Enrollment.save(this.model, this.modelId);
        this.hasErrorsInAutosave = false;
        this.lastSave = Date.now();
        this.oldModel = JSON.stringify(this.model);
        this.saving = false;
      } catch (error) {
        this.saving = false;

        // Show only one time the autosave error message
        if (!this.hasErrorsInAutosave) {
          this.hasErrorsInAutosave = true;
          this.$notify.error({
            title: "Erro",
            message: "Suas modificações não foram salvas",
          });
        }
      }
    },

    autoSaveIfNeeded() {
      this.saving = true;
      this.debouncedAutoSave();
    },

    debouncedAutoSave: _.debounce(function () {
      this.autosave();
    }, 2000),

    judgeCancelSubscription() {
      this.$confirmDialog({
        width: this.$vuetify.breakpoint.xsOnly ? "95%" : "330px",
        title: "Conte-nos o motivo do cancelamento:",
        inputType: "text",
        validationRules: { required: true, min: 10 },
      })
        .then((r) => {
          _.set(this.model.situation, "cancelReason", r);
          this.model.situation.status = { _id: "canceled" };
          this.autosave();
        })
        .catch(() => {});
    },

    postResults() {
      window.Layers.ui("createPost", {
        title: "Resultados: " + this.model.event.name,
        text: "Os resultados já estão disponíveis para download na plataforma",
        type: "message",
        roles: ["robotics:coach", "team:member"],
        targets: [
          {
            ...this.model.entity,
            type: "member",
          },
        ],
      });
    },

    moveEnrollment() {
      this.loading = true;

      Event.list({
        "event.kind": [this.nextStage.kind],
        "event.season": getCurrentSeason(this.community)._id,
      }).then(
        (res) => {
          this.loading = false;

          this.$confirmDialog({
            width: this.$vuetify.breakpoint.xsOnly ? "95%" : "330px",
            default: false,
            inputType: "select",
            title: "Classificar equipe para:",
            items: res.data.map((event) => ({
              value: event,
              text: event.name,
            })),
          })
            .then((event) => {
              if (!event) {
                return;
              }

              Enrollment.classify(event._id, this.model._id)
                .then(() => {
                  this.$notify.success({
                    title: "Erro",
                    message: "Inscrição classificada com sucesso",
                  });
                })
                .catch((err) => {
                  const status = ParseHTTPError(err);

                  this.$notify.error({
                    title: "Erro",
                    message: status.error,
                  });
                });
            })
            .catch(() => {});
        },
        () => {
          this.loading = false;
        },
      );
    },
  },
};



// WEBPACK FOOTER //
// ./src/pages/Submit/Submit.js